import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { ActionItem } from 'src/app/shared/common/components/panel-header/panel-header.component';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { MapWorkspacesStoreService } from 'src/app/shared/common/current-map-workspaces/map-workspaces-store.service';

import { JobAttachmentChipType, JobAttachmentTag } from 'src/app/shared/common/enums/attachments/job-attachment-type';
import { DetailPanelView, PanelViewMode } from 'src/app/shared/common/enums/list-panel/panel-view-mode.enum';

import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { JobSelectionCriteria } from 'src/app/shared/common/current-features/selection-criteria';
import { PanelMenuService } from 'src/app/shared/common/services/panel-menu/panel-menu.service';
import { JobFeatureDetailType } from 'src/app/shared/map-data-services/feature/job-feature-cache.service';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';
import {
    JobChipData,
    SurveyJob,
    SurveyJobAttachment,
    SurveyJobFileExtensions
} from 'src/app/shared/survey-data-services/survey-job';
import { JobCache, SurveyJobPreloadService } from 'src/app/shared/survey-data-services/survey-job-preload.service';
import { SurveyJobStoreService } from 'src/app/shared/survey-data-services/survey-job-store.service';
import { EditJobPanelComponent } from './edit-job-panel/edit-job-panel.component';

@Component({
    selector: 'survey-job-detail',
    templateUrl: 'survey-job-detail.component.html'
})
export class SurveyJobDetailComponent implements OnInit, OnDestroy {
    @Input() job: SurveyJob;
    @Output() navigateTo = new EventEmitter<PanelViewMode>();
    @Output() jobUpdated = new EventEmitter<SurveyJob>();

    @ViewChild('editJobPanel') editJobPanel: EditJobPanelComponent;

    private destroyed = new Subject<void>();
    private featurePollingInterval: any;

    ButtonType = ButtonType;
    JobAttachmentChipType = JobAttachmentChipType;
    currentviewMode: DetailPanelView = DetailPanelView.Detail;
    DetailPanelView = DetailPanelView;

    // Header tool bar
    headerActions: ActionItem[] = [];
    headerTitle: string = '';
    isEditView: boolean = false;

    jobFeatureDetails: JobFeatureDetailType;

    // Loading
    loading: boolean = true; // loads whole panel
    loadingUpdatedJob: boolean = false;
    isJobUpdating = false;

    featureCount: number | null = null;
    jobImages: JobChipData[] = [];
    jobReportsAndExports: JobChipData[] = [];
    jobOtherAttachments: JobChipData[] = [];
    tags: JobChipData[] = [];
    linkedProjectData: JobChipData[] = [];
    linkedProjectDataFiles: SurveyJobAttachment[] = [];

    constructor(
        private featuresStreams: FeaturesStreamsService,
        private panelMenuService: PanelMenuService,
        private translate: TranslationService,
        private jobPreloadService: SurveyJobPreloadService,
        private mapWorkspacesStore: MapWorkspacesStoreService,
        private surveyJobStoreService: SurveyJobStoreService,
        private messaging: MessagingService
    ) {}

    ngOnInit() {
        if (!this.job?.id) return;

        this.panelMenuService.detailsPanelViewStream.pipe(takeUntil(this.destroyed)).subscribe(view => {
            this.initHeaderActions(view);
        });
        // Check if job data is already cached
        const cachedData = this.jobPreloadService.getCachedJobData(this.job.id);
        if (cachedData && !cachedData.isLoading) {
            // Job is fully cached, use it immediately
            this.fillJobDataFromCache(cachedData);
        } else {
            // Job is not cached or still loading in background queue
            // Prioritize this user-selected job over background preloading
            this.getJobData();
        }

        // Update the feature selection criteria to filter map by job
        //this.featuresStreams.replaceOnSelectedFeatures(new JobSelectionCriteria([this.job.id], []));

        // Clear feature panel selections for survey workspace to avoid showing stale selections
        const currentWorkspace = this.mapWorkspacesStore.getCurrentMapWorkspace();
        if (currentWorkspace?.type === WorkspaceType.SURVEY) {
            this.featuresStreams.clearSelectedFeatures();
        }
    }

    updateFeaturePanelForFeatureSelection(): void {
        // Select all features in the feature panel
        this.featuresStreams.replaceOnSelectedFeatures(new JobSelectionCriteria([this.job.id], []));
    }

    fillJobDataFromCache(jobDataResult: JobCache) {
        // Use cached data
        this.job = jobDataResult.job || this.job;
        if (jobDataResult.job) {
            this.tags = this.extractTags(jobDataResult.job.tags);
        }

        if (jobDataResult.attachments) {
            this.job.attachments = jobDataResult.attachments;
            this.processAllAttachments();
        }

        if (jobDataResult.linkedData) {
            this.linkedProjectDataFiles = jobDataResult.linkedData;
            this.linkedProjectData = this.extractLinkedProjectData(jobDataResult.linkedData);
        }

        if (jobDataResult.features) {
            this.jobFeatureDetails = jobDataResult.features;
        }

        // Show the UI as soon as we have basic job data (even if features aren't loaded yet)
        // This allows for progressive loading
        if (jobDataResult.job) {
            this.loading = false;
        }
    }

    ngOnDestroy(): void {
        this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Detail);

        // Clear feature polling interval if it exists
        if (this.featurePollingInterval) {
            clearInterval(this.featurePollingInterval);
        }

        this.destroyed.next();
        this.destroyed.complete();
    }

    zoomToJobFeatures(): void {
        // Zoom to job features on the map
        this.surveyJobStoreService.zoomToJobFeatures(this.job.id);
    }

    private getJobData(): void {
        this.jobPreloadService
            .getJobData$(this.job.id)
            .then(jobDataResult => {
                this.fillJobDataFromCache(jobDataResult);
                this.jobUpdated.emit(jobDataResult.job);
                // If features aren't loaded yet, poll for them
                if (!jobDataResult.features) {
                    this.pollForFeatures();
                }
            })
            .catch(error => {
                console.error('Error getting job data:', error);
                this.loading = false;
                this.navigateTo.emit(PanelViewMode.List);
            });
    }

    /**
     * Poll for features to be loaded in the background
     */
    private pollForFeatures(): void {
        // Clear any existing polling interval
        if (this.featurePollingInterval) {
            clearInterval(this.featurePollingInterval);
        }

        this.featurePollingInterval = setInterval(() => {
            const cachedData = this.jobPreloadService.getCachedJobData(this.job.id);
            if (cachedData?.features) {
                this.jobFeatureDetails = cachedData.features;
                clearInterval(this.featurePollingInterval);
                this.featurePollingInterval = null;
            }
        }, 500); // Check every 500ms

        // Clear interval after 10 seconds to avoid infinite polling
        setTimeout(() => {
            if (this.featurePollingInterval) {
                clearInterval(this.featurePollingInterval);
                this.featurePollingInterval = null;
            }
        }, 10000);
    }

    // #region Panel Header related methods
    /**
     * Handles actions from the panel header
     */
    public handleAction(action: { item: ActionItem; event: MouseEvent }): void {
        const actionItem: ActionItem = action.item;
        if (this.loading) {
            return;
        }
        switch (actionItem.id) {
            case 'edit':
                this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Edit);
                break;
            case 'delete':
                this.deleteJob();
                break;
            default:
                console.log('Unknown action:', actionItem.id);
        }
    }

    /**
     * Initializes the header action buttons
     */
    private initHeaderActions(view: DetailPanelView): void {
        switch (view) {
            case DetailPanelView.Detail:
                this.initDetailViewHeader();
                break;
            case DetailPanelView.Edit:
                this.initEditViewHeader();
                break;
            case DetailPanelView.AttachFiles:
                this.initAttachFilesViewHeader();
                break;
        }
    }

    private initDetailViewHeader(): void {
        this.headerTitle = this.translate.instant('Survey.SurveyJobPanel.JobDetails');
        this.currentviewMode = DetailPanelView.Detail;
        this.headerActions = this.panelMenuService.getSurveyJobDetailActions();
    }

    private initEditViewHeader(): void {
        this.headerTitle = this.translate.instant('TC.Common.Edit');
        this.currentviewMode = DetailPanelView.Edit;
        this.headerActions = [];
    }

    private initAttachFilesViewHeader(): void {
        this.headerTitle = this.translate.instant('Survey.SurveyEditPanel.LinkFiles');
        this.currentviewMode = DetailPanelView.AttachFiles;
        this.headerActions = [];
    }

    /**
     * Navigation method to return to the job list
     */
    public goBackFromCurrentView(): void {
        switch (this.currentviewMode) {
            case DetailPanelView.Edit:
                this.editBackToDetailHandler();
                break;
            case DetailPanelView.AttachFiles:
                this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Edit);
                break;
            default:
                this.navigateTo.emit(PanelViewMode.List);
        }
    }

    editBackToDetailHandler(): void {
        if (this.editJobPanel && this.editJobPanel.isJobUpdated()) {
            this.editJobPanel.cancelEditAction();
        } else {
            this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Detail);
        }
    }

    // #endregion

    // #region Job Operations

    /**
     * Delete the current job
     */
    private deleteJob(): void {
        //updateCache() should be called before deleting the job
        return;
    }

    // #endregion

    // region Attachment
    /**
     * Processes all attachments and categorizes them into specific arrays
     */
    private processAllAttachments(): void {
        // Reset attachment arrays
        this.jobImages = [];
        this.jobReportsAndExports = [];
        this.jobOtherAttachments = [];

        // Skip processing if attachments are empty
        if (!this.job?.attachments?.length) {
            return;
        }

        // Process each attachment once and categorize it
        this.job.attachments.forEach(attachment => {
            const fileName = attachment.fileName?.toLowerCase() || '';
            const attachmentTag = attachment?.tags || [];

            // Create the chip data object once
            const chipData: JobChipData = {
                url: attachment.downloadUrl,
                label: attachment.fileName || `<${this.translate.instant('TC.Common.Unknown')}>`
            };

            // Check conditions for each category
            const isImage = SurveyJobFileExtensions.IMAGES.some(ext => fileName.endsWith(ext));
            const hasReportOrExportTag =
                attachmentTag.includes(JobAttachmentTag.Report.toString()) ||
                attachmentTag.includes(JobAttachmentTag.Export.toString());
            const isRootDataFile = attachmentTag.includes(JobAttachmentTag.RootDataFile.toString());

            // Categorize the attachment
            if (isImage) {
                this.jobImages.push(chipData);
            } else if (hasReportOrExportTag) {
                this.jobReportsAndExports.push(chipData);
            } else if (!isRootDataFile) {
                // Only add to "other" if it's not a root data file
                this.jobOtherAttachments.push(chipData);
            }
        });
    }
    // #endregion

    /**
     * Extracts and formats job tags into JobChipData objects
     * @param tags Array of tag strings from the job
     * @returns Array of formatted JobChipData objects for UI display
     */
    private extractTags(tags: string[]): JobChipData[] {
        if (!tags || !tags.length) {
            return [];
        }

        return tags.map(tag => ({
            label: tag
        }));
    }
    // #endregion

    /**
     * Extracts and formats linked map files data into JobChipData objects
     * @param files Array of linked map files from the API
     * @returns Array of formatted JobChipData objects for UI display
     */
    private extractLinkedProjectData(files: SurveyJobAttachment[]): JobChipData[] {
        if (!files || !files.length) {
            return [];
        }

        return files.map(file => ({
            url: file.downloadUrl,
            label: file.fileName || `<${this.translate.instant('TC.Common.Unknown')}>`
        }));
    }

    //#endregion

    // #region Job Update
    public async onJobUpdated(updatedJob: SurveyJob): Promise<void> {
        this.job = updatedJob;
        this.jobUpdated.emit(updatedJob);
        this.goBackFromCurrentView();
        const allPresent =
            this.linkedProjectDataFiles?.length === updatedJob.linkedFiles?.length &&
            updatedJob.linkedFiles?.every(updatedFile =>
                this.linkedProjectDataFiles?.some(file => file.version === updatedFile.id)
            );
        this.loadingUpdatedJob = !allPresent;
        const updatedJobCache = await this.jobPreloadService.updateJobInCache(updatedJob, !allPresent);
        this.loadingUpdatedJob = false;
        if (updatedJobCache) {
            this.fillJobDataFromCache(updatedJobCache);
        } else {
            this.messaging.showError(this.translate.instant('Survey.SurveyDetailPanel.ErrorMessage'));
        }
    }

    public setJobUpdatingState(editJobState: { isUpdating: boolean; isJobRefreshNeeded: boolean }): void {
        this.isJobUpdating = editJobState.isUpdating;
        if (editJobState.isJobRefreshNeeded) {
            this.loading = true;
            this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Detail);
            this.updateCache();
            this.getJobData();
        }
    }

    // Add this method to update cache when job is updated
    private updateCache(): void {
        if (!this.job?.id) return;

        const cachedData = this.jobPreloadService.getCachedJobData(this.job.id);
        if (cachedData) {
            this.jobPreloadService.clearCache(this.job.id);
            // The next request will re-cache it
        }
    }
    // #endregion
}
